import React, { useEffect, useRef, useState } from "react";

export const moveKey = (order, key, toIndex) => {
  const from = order.indexOf(key);
  if (from < 0) return order;
  const clamped = Math.max(0, Math.min(order.length - 1, toIndex));
  if (clamped === from) return order;
  const next = order.filter((item) => item !== key);
  next.splice(clamped, 0, key);
  return next;
};

const GripIcon = () => (
  <svg width="14" height="20" viewBox="0 0 14 20" fill="currentColor" aria-hidden="true">
    <circle cx="4" cy="4" r="1.6" />
    <circle cx="10" cy="4" r="1.6" />
    <circle cx="4" cy="10" r="1.6" />
    <circle cx="10" cy="10" r="1.6" />
    <circle cx="4" cy="16" r="1.6" />
    <circle cx="10" cy="16" r="1.6" />
  </svg>
);

const SortableBlocks = ({ order, labels, onChange, renderBlock, disabled }) => {
  const listRef = useRef(null);
  const [dragging, setDragging] = useState(null);
  // Latest props for the window listeners, so a drag does not re-subscribe on every reorder.
  const latest = useRef({ order, onChange });
  latest.current = { order, onChange };

  // Moving a block down makes React re-insert that block's own node, which drops its pointer capture.
  // So the drag listens on the window instead of relying on the grip to keep receiving events.
  useEffect(() => {
    if (!dragging) return undefined;
    const targetIndex = (clientY) =>
      [...listRef.current.querySelectorAll("[data-sort-key]")]
        .filter((node) => node.dataset.sortKey !== dragging)
        .filter((node) => {
          const rect = node.getBoundingClientRect();
          return rect.top + rect.height / 2 < clientY;
        }).length;
    const onMove = (event) => {
      const next = moveKey(latest.current.order, dragging, targetIndex(event.clientY));
      if (next !== latest.current.order) latest.current.onChange(next);
    };
    const onEnd = () => setDragging(null);
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onEnd);
    window.addEventListener("pointercancel", onEnd);
    return () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onEnd);
      window.removeEventListener("pointercancel", onEnd);
    };
  }, [dragging]);

  const onPointerDown = (key) => (event) => {
    if (disabled || event.button > 0) return;
    event.preventDefault();
    setDragging(key);
  };

  const onKeyDown = (key) => (event) => {
    if (disabled) return;
    const step = event.key === "ArrowUp" ? -1 : event.key === "ArrowDown" ? 1 : 0;
    if (!step) return;
    event.preventDefault();
    onChange(moveKey(order, key, order.indexOf(key) + step));
  };

  return (
    <ol ref={listRef} className="bio-studio__blocks">
      {order.map((key) => (
        <li key={key} data-sort-key={key} className={`bio-studio__block${dragging === key ? " is-dragging" : ""}`}>
          {renderBlock(
            key,
            <button
              type="button"
              className="bio-studio__grip"
              aria-label={`Drag to move ${labels[key]}. Use the arrow keys to move it up or down.`}
              disabled={disabled}
              onPointerDown={onPointerDown(key)}
              onKeyDown={onKeyDown(key)}
            >
              <GripIcon />
            </button>,
          )}
        </li>
      ))}
    </ol>
  );
};

export default SortableBlocks;
